import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { User, PopularityItem, PopularityCartItem, PopularityItemType } from '../types';
import { getPopularityItems, getUserByTelegramId } from '../services/api';
import { getTelegramUser } from '../services/telegram';
import { colors } from '../theme/themeConfig';
import PageHeader from '../components/layout/PageHeader';
import PopularityItemCard from '../components/popularity/PopularityItemCard';

// Image imports (WebP for better compression)
import defaultPp from '../assets/default_pp.webp';
import pp10k from '../assets/10k_pp.webp';
import pp20k from '../assets/20k_pp.webp';
import pp30k from '../assets/30k_pp.webp';
import pp40k from '../assets/40k_pp.webp';
import pp50k from '../assets/50k_pp.webp';
import pp70k from '../assets/70k_pp.webp';
import pp100k from '../assets/100k_pp.webp';
import pp200k from '../assets/200k_pp.webp';
import pp500k from '../assets/500k_pp.webp';
import pp1kk from '../assets/1kk_pp.webp';
import heli125k from '../assets/heli_125k_pp.webp';
import plane300k from '../assets/plane_300k_pp.webp';
import goldPlane500k from '../assets/gold_plane_500k_pp.webp';

// Image mapping by imageName from API
const imageMap: Record<string, string> = {
  'default_pp': defaultPp,
  '10k_pp': pp10k,
  '20k_pp': pp20k,
  '30k_pp': pp30k,
  '40k_pp': pp40k,
  '50k_pp': pp50k,
  '70k_pp': pp70k,
  '100k_pp': pp100k,
  '200k_pp': pp200k,
  '500k_pp': pp500k,
  '1kk_pp': pp1kk,
  'heli_125k_pp': heli125k,
  'plane_300k_pp': plane300k,
  'gold_plane_500k_pp': goldPlane500k,
};

// Get image for item using imageName from API
const getItemImage = (item: PopularityItem): string => {
  return imageMap[item.imageName] || defaultPp;
};

// All images for preloading
const allImages = [
  defaultPp, pp10k, pp20k, pp30k, pp40k, pp50k, pp70k,
  pp100k, pp200k, pp500k, pp1kk,
  heli125k, plane300k, goldPlane500k,
];

// Preload images into browser cache
const preloadImages = () => {
  allImages.forEach((src) => {
    const img = new Image();
    img.src = src;
  });
};

const PopularityStorePage: React.FC = () => {
  const [cart, setCart] = useState<PopularityCartItem[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [telegramUser, setTelegramUser] = useState<any>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const username = telegramUser?.username || user?.telegramUsername || 'username';

  // Fetch popularity items with React Query (cached for 5 minutes)
  const {
    data: items = [],
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['popularityItems'],
    queryFn: getPopularityItems,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const error = queryError ? 'Не удалось загрузить товары' : null;

  // Preload images on first render
  useEffect(() => {
    preloadImages();
  }, []);

  // Load saved cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('popularityCart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        localStorage.removeItem('popularityCart');
      }
    }
  }, []);

  // Initialize user data
  useEffect(() => {
    const initializeUser = async () => {
      try {
        const tgUser = getTelegramUser();
        setTelegramUser(tgUser);

        if (tgUser) {
          try {
            const userData = await getUserByTelegramId(tgUser.id);
            setUser(userData);
          } catch (err) {
            console.error('Error fetching user data:', err);
          }
        } else {
          // Fallback to default user
          const defaultUserId = 7345174833;
          try {
            const userData = await getUserByTelegramId(defaultUserId);
            setUser(userData);
          } catch (err) {
            console.error('Error fetching user data:', err);
          }
        }
      } catch (err) {
        console.error('Error initializing user:', err);
      }
    };

    initializeUser();
  }, []);

  const handleAddToCart = (item: PopularityItem) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(ci => ci.item.id === item.id);
      if (existingItem) {
        return prevCart.map(ci =>
          ci.item.id === item.id
            ? { ...ci, quantity: ci.quantity + 1 }
            : ci
        );
      }
      return [...prevCart, { item, quantity: 1 }];
    });
  };

  const handleRemoveFromCart = (itemId: string) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(ci => ci.item.id === itemId);
      if (existingItem && existingItem.quantity > 1) {
        return prevCart.map(ci =>
          ci.item.id === itemId
            ? { ...ci, quantity: ci.quantity - 1 }
            : ci
        );
      }
      return prevCart.filter(ci => ci.item.id !== itemId);
    });
  };

  // Save cart to localStorage when it changes
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem('popularityCart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('popularityCart');
    }
  }, [cart]);

  const getItemQuantity = (itemId: string): number => {
    const cartItem = cart.find(ci => ci.item.id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  const calculateTotal = (): number => {
    return cart.reduce((total, ci) => total + (ci.item.price * ci.quantity), 0);
  };

  const calculateTotalPP = (): number => {
    return cart.reduce((total, ci) => total + (ci.item.ppAmount * ci.quantity), 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    navigate('/shop', { state: { orderType: 'popularity', popularityCart: cart } });
  };

  // Sort items: PP bundles first, then special items (memoized)
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.itemType === b.itemType) return 0;
      return a.itemType === PopularityItemType.PP_BUNDLE ? -1 : 1;
    });
  }, [items]);

  const hasSelectedItems = cart.length > 0;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: theme.spacing(2),
        backgroundColor: colors.background.default,
        color: colors.text.primary,
        minHeight: '100vh',
        position: 'relative',
        paddingBottom: hasSelectedItems ? theme.spacing(20) : theme.spacing(10),
      }}
    >
      <PageHeader
        username={username}
        balance={user?.bonusBalance || 0}
        photoUrl={telegramUser?.photoUrl}
      />

      {/* Info notification */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.main}`,
          borderRadius: '12px',
        }}
      >
        <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 0.5 }}>
          ✅ Покупка доступна 24/7
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 0.5 }}>
          🕒 Среднее время доставки 1-15 минут
        </Typography>
        <Typography variant="body2" sx={{ color: colors.secondary.main, fontWeight: 500 }}>
          ❗️ Не оформляйте заказ, если до конца раунда осталось менее 15 минут
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: isMobile ? '8px' : '16px',
          width: '100%',
          marginBottom: theme.spacing(3),
        }}
      >
        {sortedItems.map(item => (
          <PopularityItemCard
            key={item.id}
            item={item}
            quantity={getItemQuantity(item.id)}
            imageSrc={getItemImage(item)}
            bonusPoints={item.bonusPoints}
            onAdd={() => handleAddToCart(item)}
            onRemove={() => handleRemoveFromCart(item.id)}
          />
        ))}
      </Box>

      {/* Floating Checkout Button */}
      {hasSelectedItems && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: theme.spacing(2),
            paddingBottom: '96px',
            backgroundColor: colors.background.default,
            borderTop: `1px solid ${colors.primary.main}`,
            zIndex: 99,
            boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleCheckout}
            sx={{
              borderRadius: theme.shape.borderRadius,
              padding: theme.spacing(1.5),
              fontSize: theme.typography.pxToRem(16),
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            Купить {calculateTotalPP().toLocaleString()} PP за {calculateTotal()} рублей
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PopularityStorePage;
